import React, { useEffect, useRef, useState } from "react";
import { List, LayoutGrid, Trophy, Star } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { getDoc, getDocs, doc, query, where, limit } from "firebase/firestore";
import { paths } from "../lib/paths";
import { col, ref } from "../lib/fw";
import { db } from "../lib/firebase";
import Panel from "../central/Panel";
import Pitch from "../central/Pitch";
import PlayerPointsModal from "../components/PlayerPointsModal";
import { HeadingMedium, HeadingSmall, BodyText } from "../central/Typography";
import { useAuth } from "../context/AuthContext";
import { getLeagueConfig } from "../utils/leagueConfig";
import { isUnlimitedFT } from "../utils/freeTransfers";
import { selectDreamTeam } from "../utils/dreamTeamUtils";
import { CANON_TO_LABEL } from "../utils/scoringRules";
import { computeMatchResult, RESULT_COLORS } from "../components/matchInput/MatchScoreStep";
import "../central/PitchPages.css";
import PageLoader from "../components/PageLoader";
import { useSeason } from "../context/SeasonContext";
// SeasonSelector replaced with local pills (pageSeasonID isolated from global context)

// --- Transfer helpers (mirror Leaderboard.js) ---
function normalizemaxFreeTransfers(team, gwID, leagueConfig) {
  const tfRaw = team?.transfersFree?.[gwID];
  if (isUnlimitedFT(tfRaw)) return Infinity;
  if (tfRaw !== undefined) return Number(tfRaw);
  return leagueConfig.maxFreeTransfers ?? 0;
}


function computeTransferPenalty(team, gwID, leagueConfig, gameweeks, positions) {
  // Use explicit Firestore schema for transfers: { GW2: 2 }
  const used = Number(team?.transfersMade?.[gwID] ?? 0);
  const free = normalizemaxFreeTransfers(team, gwID, leagueConfig);
  const penalty = leagueConfig.transferPenaltyPoints || 5;
  const over = free !== Infinity && used > free ? used - free : 0;
  const penaltyPoints = over * penalty;
  console.log("🔎 Transfer penalty debug", { gwID, used, free, over, penaltyPoints, transfersMade: team?.transfersMade, transfersFree: team?.transfersFree });
  return { penaltyPoints, used, free, over };
}

// Colour for a points value
function ptColor(pts) {
  if (pts > 3) return "#22c55e";
  if (pts < 2) return "#ef4444";
  return "#9ca3af";
}

export default function YourPoints() {
  const { leagueID, teamID: routeTeamID } = useParams();
  const { currentUser } = useAuth();
  const { availableSeasons, selectedSeasonID, currentSeasonID } = useSeason();
  // Local season state — isolated from global context, same pattern as DreamTeam.js
  const [pageSeasonID, setPageSeasonID] = useState(selectedSeasonID);
  const isAutoInit = useRef(true);
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('pitch');


  const [resolvedTeamID, setResolvedTeamID] = useState(routeTeamID || null);
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerPoints, setPlayerPoints] = useState({}); // map of playerId -> full doc (total + breakdown/raw)
  const [gameweeks, setGameweeks] = useState([]);
  const [selectedGW, setSelectedGW] = useState("");
  const [loading, setLoading] = useState(true);
  const [openBreakdown, setOpenBreakdown] = useState({}); // playerId -> bool
  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  const [activePointsPlayerID, setActivePointsPlayerID] = useState(null);
  const [modalPlayerIDs, setModalPlayerIDs] = useState([]);
  const [leagueType, setLeagueType] = useState("7-a-side");
  const [leagueName, setLeagueName] = useState("");
  const [gwManagerRank, setGwManagerRank] = useState(null);
  const [gwMatchResults, setGwMatchResults] = useState([]); // match docs for selectedGW with both scores present

  function pointsWithChipAndCaptain({
    pid,
    isCaptain,
    chipUsed,
    playerPointsMap
  }) {
    const rec = playerPointsMap[pid] || { total: 0, breakdown: {} };
    const b = rec.breakdown || {};
    const base = Number(rec.total) || 0;
  
    // chip extras (added before captain multiplier)
    let chipExtra = 0;
    let chipNote = "";
  
    if (chipUsed === "Goal Boost") {
      const ga = (Number(b.goals) || 0) + (Number(b.assists) || 0);
      chipExtra += ga;
      if (ga) chipNote = `Goal Boost: +${ga} (goals+assists doubled)`;
    } else if (chipUsed === "Fortress") {
      const cs = (Number(b.cleanSheet) || 0) + (Number(b.cleanSheetGK) || 0);
      chipExtra += cs;
      if (cs) chipNote = `Fortress: +${cs} (clean sheet doubled)`;
    }
  
    // captain multiplier
    const captainMult = isCaptain ? (chipUsed === "Triple Captain" ? 3 : 2) : 1;
    const final = (base + chipExtra) * captainMult;
  
    return {
      base,
      chipExtra,
      captainMult,
      final,
      chipNote
    };
  }

  // Resolve teamID from users/{uid}.teams[leagueID] if not in route
  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!leagueID) return;
      if (routeTeamID) {
        if (isMounted) setResolvedTeamID(routeTeamID);
        return;
      }
      if (currentUser?.uid) {
        try {
          const uSnap = await getDoc(doc(db, "users", currentUser.uid));
          const u = uSnap.exists() ? uSnap.data() : null;
          const tid = u?.teams?.[leagueID] || null;
          if (isMounted) setResolvedTeamID(tid);
        } catch (e) {
          console.error("Failed to resolve teamID from user doc:", e);
          if (isMounted) setResolvedTeamID(null);
        }
      } else {
        if (isMounted) setResolvedTeamID(null);
      }
    })();
    return () => { isMounted = false; };
  }, [leagueID, routeTeamID, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load gameweeks for a given season and return { list, defaultGW, hasData }
  // hasData = most recent GW has playerPoints documents
  const fetchGameweeksForSeason = async (seasonID, leagueCurrentGW) => {
    const gwSnap = await getDocs(paths.leagueGameweeks(leagueID, seasonID));
    const rows = gwSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const sorted = rows
      .filter((r) => r.id !== "meta")
      .map((r) => ({ ...r, num: Number(r.number) || 0 }))
      .sort((a, b) => a.num - b.num);
    // Only exclude the current in-progress GW when viewing the current season
    let list = sorted
      .map((r) => r.id)
      .filter((id) => seasonID === currentSeasonID ? id !== leagueCurrentGW : true);

    // If excluding the current GW leaves nothing (e.g. GW1 of a new season,
    // where the current GW is the only gameweek that exists), fall back to
    // including it so selectedGW still resolves and players/team data loads.
    if (list.length === 0 && seasonID === currentSeasonID && leagueCurrentGW) {
      list = sorted.map((r) => r.id);
    }

    // Compute the sensible default GW
    const rawGW = leagueCurrentGW || (sorted.length ? sorted[sorted.length - 1].id : "GW1");
    const gwNum = Number(String(rawGW).replace("GW", "")) || 1;
    const prevGW = gwNum > 1 ? `GW${gwNum - 1}` : rawGW;
    const defaultGW = list.includes(prevGW) ? prevGW : (list[list.length - 1] || null);

    // Check if this season has any processed results (check last GW for playerPoints)
    let hasData = false;
    if (list.length > 0) {
      try {
        const ptSnap = await getDocs(
          query(paths.gwPlayerPoints(leagueID, list[list.length - 1], seasonID), limit(1))
        );
        hasData = !ptSnap.empty;
      } catch { /* skip */ }
    }

    return { list, defaultGW, hasData };
  };

  // Load gameweeks, choose selectedGW, and auto-fallback to prior season if no data
  useEffect(() => {
    if (!leagueID || !pageSeasonID || !availableSeasons.length) return;

    let cancelled = false;
    (async () => {
      try {
        let leagueCurrentGW = null;
        try {
          const leagueSnap = await getDoc(paths.league(leagueID));
          if (leagueSnap.exists()) leagueCurrentGW = leagueSnap.data().currentGW || null;
        } catch {}

        const { list, defaultGW, hasData } = await fetchGameweeksForSeason(pageSeasonID, leagueCurrentGW);
        if (cancelled) return;

        // Auto-fallback on first load: if this season has no processed results, try prior seasons
        if (!hasData && isAutoInit.current) {
          const priorSeasons = [...availableSeasons].reverse().filter((s) => s !== pageSeasonID);
          for (const s of priorSeasons) {
            const { list: sList, defaultGW: sGW, hasData: sHasData } = await fetchGameweeksForSeason(s, leagueCurrentGW);
            if (cancelled) return;
            if (sHasData) {
              setPageSeasonID(s);
              setGameweeks(sList);
              setSelectedGW(sGW);
              return; // re-render; effect re-runs with new pageSeasonID (isAutoInit false by then)
            }
          }
        }

        if (cancelled) return;
        setGameweeks(list);
        setSelectedGW(defaultGW);
      } catch (e) {
        if (!cancelled) console.error("Failed to load gameweeks:", e);
      } finally {
        if (!cancelled) isAutoInit.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID, pageSeasonID, availableSeasons]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load team (GW snapshot then fallback), players, league, and detailed points for selectedGW
  useEffect(() => {
    if (!leagueID) return;
    if (!selectedGW) {
      // No valid GW for this season — clear any stale data from a previous season/GW
      setPlayerPoints({});
      setTeam(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // TEAM
        if (resolvedTeamID) {
          const gwSnap = await getDoc(
            doc(paths.gwTeams(leagueID, selectedGW, pageSeasonID), resolvedTeamID)
          );
          const rootSnap = await getDoc(
            doc(paths.leagueTeams(leagueID, pageSeasonID), resolvedTeamID)
          );

          // Merge: root fields first, then gwSnap fields override (GW snapshot is source of truth for selections/squadMeta)
          const mergedData = {
            ...(rootSnap.exists() ? rootSnap.data() : {}),
            ...(gwSnap.exists() ? gwSnap.data() : {}),
          };
          console.log("🔎 Merged team data", mergedData);

          if (cancelled) return;
          setTeam({ id: resolvedTeamID, ...mergedData });
        } else {
          if (cancelled) return;
          setTeam(null);
        }

        // PLAYERS
        const playersCol = paths.leaguePlayers(leagueID, pageSeasonID);
        const pSnap = await getDocs(playersCol);
        if (cancelled) return;
        const plist = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlayers(plist);

        // LEAGUE
        const leagueSnap = await getDoc(paths.league(leagueID));
        if (cancelled) return;
        const leagueData = leagueSnap.exists() ? leagueSnap.data() : {};
        setLeagueType(leagueData.leagueType || "7-a-side");
        setLeagueName(leagueData.name || "");

        // POINTS
        const ptsSnap = await getDocs(
          paths.gwPlayerPoints(leagueID, selectedGW, pageSeasonID)
        );
        if (cancelled) return;
        const pts = {};
        ptsSnap.forEach((d) => {
          const data = d.data() || {};
          pts[d.id] = {
            total: Number(data.total) || 0,
            breakdown: data.breakdown || {}
          };
        });
        setPlayerPoints(pts);

        // MATCH RESULTS — only matches that have both scores recorded
        try {
          const matchSnap = await getDocs(paths.gwMatches(leagueID, selectedGW, pageSeasonID));
          if (cancelled) return;
          const matchesWithScores = matchSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((m) => m?.yourScore !== undefined && m?.yourScore !== null && m?.theirScore !== undefined && m?.theirScore !== null);
          setGwMatchResults(matchesWithScores);
        } catch (e) {
          if (cancelled) return;
          console.error("Failed to fetch match results:", e);
          setGwMatchResults([]);
        }
      } catch (e) {
        if (!cancelled) console.error("Failed to fetch team/players/points:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueID, selectedGW, resolvedTeamID, pageSeasonID]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute this manager's GW rank vs all teams in the league
  useEffect(() => {
    if (!leagueID || !selectedGW || !resolvedTeamID || Object.keys(playerPoints).length === 0) {
      setGwManagerRank(null);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        // Helper: compute player final score (mirrors Leaderboard.js)
        const computePlayerFinal = (pid, captainID, chipUsed, ptsMap) => {
          const rec = ptsMap[pid] || { total: 0, breakdown: {} };
          const base = Number(rec.total) || 0;
          const b = rec.breakdown || {};
          let chipExtra = 0;
          if (chipUsed === "Goal Boost") chipExtra += (Number(b.goals) || 0) + (Number(b.assists) || 0);
          else if (chipUsed === "Fortress") chipExtra += (Number(b.cleanSheet) || 0) + (Number(b.cleanSheetGK) || 0);
          const mult = pid === captainID ? (chipUsed === "Triple Captain" ? 3 : 2) : 1;
          return (base + chipExtra) * mult;
        };

        const computeGWPoints = (teamDoc, ptsMap) => {
          if (!teamDoc) return 0;
          const sel = teamDoc.selections?.[selectedGW] || { DEF: [], MID: [], FWD: [] };
          const picks = ["DEF", "MID", "FWD"].flatMap((k) => sel[k] || []);
          const cap = teamDoc.captains?.[selectedGW] ?? teamDoc.captain ?? null;
          const chip = teamDoc.chipsUsed?.[selectedGW] ?? teamDoc.chip ?? teamDoc.chips?.[selectedGW] ?? "None";
          return picks.reduce((sum, pid) => sum + computePlayerFinal(pid, cap, chip, ptsMap), 0);
        };

        // Fetch all team docs for this GW
        const teamsSnap = await getDocs(paths.leagueTeams(leagueID, pageSeasonID));
        const teamDocs = await Promise.all(
          teamsSnap.docs.map(async (td) => {
            const gwSnap = await getDoc(doc(paths.gwTeams(leagueID, selectedGW, pageSeasonID), td.id));
            const data = gwSnap.exists()
              ? { ...td.data(), ...gwSnap.data() }
              : td.data();
            return { id: td.id, ...data };
          })
        );

        // Score each team and sort
        const scored = teamDocs.map((td) => ({
          id: td.id,
          pts: computeGWPoints(td, playerPoints),
        })).sort((a, b) => b.pts - a.pts);

        // Find rank of current team (dense rank)
        let rank = null;
        let currentRank = 1;
        for (let i = 0; i < scored.length; i++) {
          if (i > 0 && scored[i].pts < scored[i - 1].pts) currentRank = i + 1;
          if (scored[i].id === resolvedTeamID) { rank = currentRank; break; }
        }

        if (isMounted) setGwManagerRank(rank);
      } catch (e) {
        console.error("Failed to compute GW manager rank:", e);
        if (isMounted) setGwManagerRank(null);
      }
    })();
    return () => { isMounted = false; };
  }, [leagueID, selectedGW, resolvedTeamID, pageSeasonID, playerPoints]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <>
        <PageLoader />
      </>
    );
  }

  // Nothing to resolve yet (e.g. S1 GW1 before any team/matches exist) —
  // show one clean message instead of a half-resolved GW selector, team
  // messaging, and All Players section. This naturally stops appearing
  // once gameweeks actually load.
  if (gameweeks.length === 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "3rem", padding: "0 1rem" }}>
        <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⚽</div>
        <div style={{ fontSize: "1rem", color: "#9ca3af", fontWeight: "500" }}>
          No matches yet
        </div>
      </div>
    );
  }

  const leagueConfig = getLeagueConfig(leagueType);
  const positions = leagueConfig.positions || ["DEF", "MID", "FWD"];

  // Dream team: pure client-side calculation from already-loaded playerPoints.
  // Only computed when points data exists (i.e. GW has been processed).
  const dreamTeamSet = (() => {
    if (!leagueConfig.squadConfig || players.length === 0 || Object.keys(playerPoints).length === 0)
      return new Set();
    const pointsMap = {};
    players.forEach((p) => { pointsMap[p.id] = Number(playerPoints[p.id]?.total ?? 0); });
    const result = selectDreamTeam(players, pointsMap, leagueConfig.squadConfig);
    return new Set(Object.values(result).flat());
  })();
  const teamName = team?.name || "Team";
  const leagueNameLabel = leagueName || "League";

  // Build squad (supports both schemas)
  const selectionsObj =
    team?.selections?.[selectedGW] || positions.reduce((acc, pos) => ({ ...acc, [pos]: [] }), {});

  const flatIds =
    team?.players && Array.isArray(team.players)
      ? team.players
      : positions.flatMap((pos) => selectionsObj[pos] || []);

  const captainID =
    team?.captains?.[selectedGW] ??
    team?.captain ??
    null;

  const chipUsed =
    team?.chipsUsed?.[selectedGW] ??
    team?.chip ??
    team?.chips?.[selectedGW] ??
    "None";



  const squadWithPoints = flatIds.map((pid) => {
    const p = players.find((pl) => pl.id === pid);
    const isCaptain = pid === captainID;

    const calc = pointsWithChipAndCaptain({
      pid,
      isCaptain,
      chipUsed,
      playerPointsMap: playerPoints
    });

    return {
      id: pid,
      name: p?.name || "Unknown",
      position: p?.position || "—",
      base: calc.base,
      final: calc.final,
      chipNote: calc.chipNote,
      captainMult: calc.captainMult,
      breakdown: playerPoints[pid]?.breakdown || {},
      isCaptain
    };
});

  // Calculate transfer penalty using helpers (mirror Leaderboard.js)
  const { penaltyPoints: transferPenalty, over: transfersOver } =
    computeTransferPenalty(team, selectedGW, leagueConfig, gameweeks, positions);

  const total = squadWithPoints.reduce((sum, p) => sum + p.final, 0) - transferPenalty;
  const noSquad = flatIds.length === 0;

  const squadModalPlayerIDs = squadWithPoints.map((p) => p.id);
  const allPlayersModalPlayerIDs = players
    .slice()
    .sort((a, b) => Number(playerPoints[b.id]?.total ?? 0) - Number(playerPoints[a.id]?.total ?? 0))
    .map((p) => p.id);

  const toggleBreakdown = (pid) =>
    setOpenBreakdown((prev) => ({ ...prev, [pid]: !prev[pid] }));

  const openPointsModal = (playerOrId, playerIDs = []) => {
    const playerID = typeof playerOrId === "string" ? playerOrId : playerOrId?.id;
    if (!playerID) return;
    setActivePointsPlayerID(playerID);
    setModalPlayerIDs(playerIDs);
    setPointsModalOpen(true);
  };

  const closePointsModal = () => {
    setPointsModalOpen(false);
    setActivePointsPlayerID(null);
    setModalPlayerIDs([]);
  };

  const activeModalIndex = activePointsPlayerID ? modalPlayerIDs.indexOf(activePointsPlayerID) : -1;
  const canPrevModalPlayer = activeModalIndex > 0;
  const canNextModalPlayer = activeModalIndex >= 0 && activeModalIndex < modalPlayerIDs.length - 1;

  const goToPrevModalPlayer = () => {
    if (!canPrevModalPlayer) return;
    setActivePointsPlayerID(modalPlayerIDs[activeModalIndex - 1]);
  };

  const goToNextModalPlayer = () => {
    if (!canNextModalPlayer) return;
    setActivePointsPlayerID(modalPlayerIDs[activeModalIndex + 1]);
  };

  const renderBreakdown = (p) => {
    // We render whichever structure is present
    const b = p.breakdown || {};
    const r = p.raw || {};

    // Make a friendly list from breakdown (points) and raw (event counts)
    const lines = [];

    // Helper to format breakdown keys
    const formatBreakdownKey = (k) => {
      if (!k) return "";
      // special-case
      let s = k === "cleanSheet" ? "clean sheet" : String(k);
      // camelCase -> spaces, underscores/hyphens -> spaces
      s = s
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[\-_]+/g, " ")
        .trim();
      // Title Case
      return s
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };

    // Prefer explicit breakdown if your scoring job provides it:
    Object.keys(b).forEach((k) => {
      const pts = b[k];
      if (pts === 0 || pts === undefined || pts === null) return;
      const label = formatBreakdownKey(k);
      lines.push(`${label}: ${pts > 0 ? "+" : ""}${pts}`);
    });

    // Also show raw tallies (goals, assists, cs, cards, etc.) if present
    const rawPairs = [
      ["goals", r.goals],
      ["assists", r.assists],
      ["clean sheet", r.cleanSheet ? 1 : r.cs], // booleans or numeric
      ["conceded", r.conceded],
      ["minutes", r.minutes],
      ["saves", r.saves],
      ["yellow cards", r.yc ?? r.yellowCards],
      ["red cards", r.rc ?? r.redCards],
    ];
    rawPairs.forEach(([label, val]) => {
      if (val !== undefined && val !== null && val !== 0 && val !== false) {
        lines.push(`${formatBreakdownKey(label)}: ${val}`);
      }
    });

    if (p.chipNote) lines.push(`chip: ${p.chipNote}`);
    if (p.isCaptain) lines.push(`captain: ×${p.captainMult}`);

    if (lines.length === 0) return <em>No breakdown available for this player.</em>;
    return (
      <ul style={{ margin: "6px 0 0 18px" }}>
        {lines.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    );
  };

  // Build playersById and selection for Pitch (mirror ViewTeam.js)
  const playersById = {};
  players.forEach((p) => {
    playersById[p.id] = p;
  });

  const selection = positions.reduce((acc, pos) => {
    acc[pos] = flatIds
      .map((pid) => players.find((p) => p.id === pid))
      .filter((p) => p && p.position === pos)
      .map((p) => p.id);
    return acc;
  }, {});

  const activePointsPlayer = activePointsPlayerID ? players.find((p) => p.id === activePointsPlayerID) || null : null;
  const activePointsDoc = activePointsPlayerID ? playerPoints[activePointsPlayerID] || {} : {};
  const activePointsBreakdown = activePointsDoc?.breakdown || {};
  const activePointsRaw = activePointsDoc?.raw || {};
  const activePointsIsCaptain = activePointsPlayerID === captainID;

  const activePointsChipCalc = activePointsPlayerID
    ? pointsWithChipAndCaptain({
        pid: activePointsPlayerID,
        isCaptain: activePointsIsCaptain,
        chipUsed,
        playerPointsMap: playerPoints,
      })
    : { chipNote: "", chipExtra: 0, captainMult: 1, base: 0, final: 0 };

  const activePointsBreakdownRows = activePointsPlayerID
    ? [
        ...Object.entries(activePointsBreakdown)
          .filter(([key, val]) => {
            if (val === 0 || val === undefined || val === null || val === false) return false;
            if (["isGK", "isGoalkeeper"].includes(key)) return false;
            return true;
          })
          .map(([key, val]) => {
            const normalizedKey = String(key).toLowerCase();

            const fallbackMap = {
              cleansheet: "Clean Sheet",
              cleansheetgk: "Clean Sheet",
              yc: "Yellow Card",
              yellowcards: "Yellow Card",
              rc: "Red Card",
              redcards: "Red Card",
              owngoals: "Own Goal",
              owngoal: "Own Goal",
            };

            let label =
              CANON_TO_LABEL[normalizedKey] ||
              fallbackMap[normalizedKey] ||
              String(key);

            label = label
              .replace(/([a-z])([A-Z])/g, "$1 $2")
              .replace(/[\-_]+/g, " ")
              .trim()
              .split(/\s+/)
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ");

            return {
              label,
              value: `${Number(val) > 0 ? "+" : ""}${val}`,
            };
          }),
        ...(activePointsChipCalc.chipExtra > 0
          ? [
              {
                label: chipUsed,
                value: `+${activePointsChipCalc.chipExtra}`,
              },
            ]
          : []),
        ...(activePointsIsCaptain
          ? [
              {
                label: chipUsed === "Triple Captain" ? "Triple Captain" : "Captain Multiplier",
                value: `×${activePointsChipCalc.captainMult}`,
              },
            ]
          : []),
      ]
    : [];

  const gwRankMap = (() => {
    const ranked = players
      .map((p) => ({
        id: p.id,
        total: Number(playerPoints[p.id]?.total ?? 0),
      }))
      .sort((a, b) => b.total - a.total);

    const rankMap = new Map();
    let currentRank = 1;
    ranked.forEach((entry, index) => {
      if (index > 0 && entry.total < ranked[index - 1].total) {
        currentRank = index + 1;
      }
      rankMap.set(entry.id, currentRank);
    });
    return rankMap;
  })();

  const positionGwRankMap = (() => {
    const rankMap = new Map();
    const uniquePositions = [...new Set(players.map((p) => p.position).filter(Boolean))];

    uniquePositions.forEach((pos) => {
      const ranked = players
        .filter((p) => p.position === pos)
        .map((p) => {
          const calc = pointsWithChipAndCaptain({
            pid: p.id,
            isCaptain: p.id === captainID,
            chipUsed,
            playerPointsMap: playerPoints,
          });

          return {
            id: p.id,
            total: Number(calc.final ?? 0),
          };
        })
        .sort((a, b) => b.total - a.total);

      let currentRank = 1;
      ranked.forEach((entry, index) => {
        if (index > 0 && entry.total < ranked[index - 1].total) {
          currentRank = index + 1;
        }
        rankMap.set(entry.id, currentRank);
      });
    });

    return rankMap;
  })();

  const formRankMap = (() => {
    const ranked = players
      .map((p) => {
        const vals = Object.keys(p?.pointsHistory || {})
          .sort((a, b) => {
            const aNum = Number(String(a).replace("GW", "")) || 0;
            const bNum = Number(String(b).replace("GW", "")) || 0;
            return aNum - bNum;
          })
          .slice(-3)
          .map((gw) => Number(p?.pointsHistory?.[gw] || 0));

        const total = vals.reduce((sum, val) => sum + val, 0);
        return { id: p.id, total };
      })
      .sort((a, b) => b.total - a.total);

    const rankMap = new Map();
    let currentRank = 1;
    ranked.forEach((entry, index) => {
      if (index > 0 && entry.total < ranked[index - 1].total) {
        currentRank = index + 1;
      }
      rankMap.set(entry.id, currentRank);
    });
    return rankMap;
  })();

  const seasonRankMap = (() => {
    const ranked = players
      .map((p) => ({
        id: p.id,
        total: Number(p?.stats?.totalPoints || 0),
      }))
      .sort((a, b) => b.total - a.total);

    const rankMap = new Map();
    let currentRank = 1;
    ranked.forEach((entry, index) => {
      if (index > 0 && entry.total < ranked[index - 1].total) {
        currentRank = index + 1;
      }
      rankMap.set(entry.id, currentRank);
    });
    return rankMap;
  })();

  const seasonPositionRankMap = (() => {
    const rankMap = new Map();
    const uniquePositions = [...new Set(players.map((p) => p.position).filter(Boolean))];

    uniquePositions.forEach((pos) => {
      const ranked = players
        .filter((p) => p.position === pos)
        .map((p) => ({
          id: p.id,
          total: Number(p?.stats?.totalPoints || 0),
        }))
        .sort((a, b) => b.total - a.total);

      let currentRank = 1;
      ranked.forEach((entry, index) => {
        if (index > 0 && entry.total < ranked[index - 1].total) {
          currentRank = index + 1;
        }
        rankMap.set(entry.id, currentRank);
      });
    });

    return rankMap;
  })();

  // --- Gameweek pill selector with arrow navigation ---
  function GWSelectorPill() {
    const currentIndex = gameweeks.indexOf(selectedGW);
    const canPrev = currentIndex > 0;
    const canNext = currentIndex >= 0 && currentIndex < gameweeks.length - 1;
    const arrowBtn = (enabled) => ({
      background: "none",
      border: "none",
      cursor: enabled ? "pointer" : "default",
      padding: "0.3rem 0.6rem",
      fontSize: "1rem",
      color: enabled ? "#50B4C8" : "#ccc",
      fontWeight: "700",
      lineHeight: 1,
    });
    // Always render — show "No GWs" label when season has no gameweeks yet
    return (
      <div style={{ display: "inline-flex", alignItems: "center", background: "#f4f7f8", borderRadius: "999px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <button style={arrowBtn(canPrev)} disabled={!canPrev} onClick={() => canPrev && setSelectedGW(gameweeks[currentIndex - 1])} aria-label="Previous gameweek">←</button>
        <span style={{ fontWeight: 600, fontSize: "0.85rem", minWidth: 44, textAlign: "center", color: "#222", padding: "0 0.1rem" }}>
          {selectedGW || (gameweeks.length === 0 ? "No GWs" : "—")}
        </span>
        <button style={arrowBtn(canNext)} disabled={!canNext} onClick={() => canNext && setSelectedGW(gameweeks[currentIndex + 1])} aria-label="Next gameweek">→</button>
      </div>
    );
  }

  return (
    <>
      <div>
        {availableSeasons.length > 1 && (
          <div style={{ padding: "0.75rem 1rem 0" }}>
            <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
              {availableSeasons.map((s) => (
                <button
                  key={s}
                  onClick={() => { isAutoInit.current = false; setPageSeasonID(s); setSelectedGW(null); }}
                  style={{
                    background: s === pageSeasonID ? "#50B4C8" : "#f4f7f8",
                    color: s === pageSeasonID ? "#fff" : "#222",
                    border: "1px solid #e5e7eb",
                    borderRadius: "999px",
                    padding: "0.3rem 0.85rem",
                    fontSize: "0.82rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {routeTeamID && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: "0.5rem", margin: "0.5rem auto 0", padding: "0.4rem 0.9rem",
            background: "#eaf7f9", border: "1px solid #50B4C8",
            borderRadius: "999px", maxWidth: 320, fontSize: "0.82rem",
            color: "#0d6b7a", fontWeight: 600
          }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: "none", border: "none", color: "#50B4C8",
                fontWeight: 700, cursor: "pointer", padding: 0, fontSize: "0.9rem" }}
              aria-label="Back"
            >
              ←
            </button>
            <span>Viewing {teamName}</span>
          </div>
        )}
        {gwMatchResults.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "0.4rem", margin: "0.5rem auto 0" }}>
            {gwMatchResults.map((m) => {
              const resultValue = m.result || computeMatchResult(m.yourScore, m.theirScore);
              const color = resultValue ? RESULT_COLORS[resultValue] : "#50B4C8";
              const opponentLabel = m.opponent && String(m.opponent).trim() !== "" ? m.opponent : "Opponent";
              return (
                <div
                  key={m.id}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0.3rem 0.85rem", background: `${color}1A`, border: `1px solid ${color}`,
                    borderRadius: "999px", fontSize: "0.82rem", color, fontWeight: 600, whiteSpace: "nowrap",
                  }}
                >
                  vs {opponentLabel}: {m.yourScore}–{m.theirScore}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "0.5rem", marginBottom: "8px", position: "relative" }}>
          <GWSelectorPill />
          <div style={{ position: "absolute", right: "0.5rem" }}>
            {activeView === "list" ? (
              <button
                onClick={() => setActiveView("pitch")}
                style={{ background: "#fff", border: "none", borderRadius: "8px", padding: "6px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", cursor: "pointer", color: "#50B4C8", display: "flex", alignItems: "center" }}
                aria-label="Switch to pitch view"
              >
                <LayoutGrid size={18} />
              </button>
            ) : (
              <button
                onClick={() => setActiveView("list")}
                style={{ background: "#fff", border: "none", borderRadius: "8px", padding: "6px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", cursor: "pointer", color: "#50B4C8", display: "flex", alignItems: "center" }}
                aria-label="Switch to list view"
              >
                <List size={18} />
              </button>
            )}
          </div>
        </div>
          {activeView === "list" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                maxWidth: 600,
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  marginTop: "0.1rem",
                  width: "100%",
                  maxWidth: 600,
                  margin: "0 auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // minWidth removed
                }}
              >
                {/* Messages */}
                {!resolvedTeamID ? (
                  <div style={{ textAlign: "center", marginTop: "2.5rem", padding: "0 1rem" }}>
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>👋</div>
                    <div style={{ fontSize: "1rem", color: "#9ca3af", fontWeight: "500" }}>
                      No team linked to your account for this league
                    </div>
                  </div>
                ) : !team ? (
                  <div style={{ textAlign: "center", marginTop: "2.5rem", padding: "0 1rem" }}>
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⚠️</div>
                    <div style={{ fontSize: "1rem", color: "#9ca3af", fontWeight: "500" }}>
                      No team document found
                    </div>
                  </div>
                ) : noSquad ? (
                  <div style={{ textAlign: "center", marginTop: "2.5rem", padding: "0 1rem" }}>
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📝</div>
                    <div style={{ fontSize: "1rem", color: "#9ca3af", fontWeight: "500" }}>
                      No selections saved
                    </div>
                  </div>
                ) : null}

                {/* Empty GW — no playerPoints data yet */}
                {resolvedTeamID && team && !noSquad && Object.keys(playerPoints).length === 0 && (
                  <div style={{ textAlign: "center", marginTop: "2.5rem", padding: "0 1rem" }}>
                    <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⏳</div>
                    <div style={{ fontSize: "1rem", color: "#9ca3af", fontWeight: "500" }}>
                      No results for this gameweek yet
                    </div>
                  </div>
                )}

                {/* Team Table + All Players in one Panel */}
                {(Object.keys(playerPoints).length > 0 || noSquad) && (
                <Panel
                  style={{
                    marginTop: "0.1rem",
                    boxShadow: "none",
                    border: "none",
                    width: "100%",
                    maxWidth: 600
                  }}
                >
                  {noSquad ? null : (
                    <>
                      {/* Hero score card */}
                      <div style={{ borderTop: "3px solid #50B4C8", borderRadius: "10px 10px 0 0", margin: "0 -1px", padding: "1.1rem 0 0.85rem", textAlign: "center" }}>
                        <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "48px", fontWeight: "800", lineHeight: 1, color: "#222", letterSpacing: "-1px" }}>
                          {total}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#9ca3af", marginTop: "4px", fontWeight: 500 }}>
                          {selectedGW} Points{gwManagerRank ? ` • #${gwManagerRank} this GW` : ""}
                        </div>
                        {transferPenalty > 0 && (
                          <div style={{ fontSize: "0.78rem", color: "#ef4444", marginTop: "3px" }}>
                            −{transferPenalty} transfer penalty ({transfersOver} over)
                          </div>
                        )}
                      </div>
                      <BodyText style={{ margin: "0.65rem 0 0.4rem", fontSize: "0.82rem", color: "#666" }}>
                        Tap a player for full breakdown
                      </BodyText>
                      <table
                        cellPadding="0"
                        cellSpacing="0"
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr style={{ borderBottom: "none", background: "transparent" }}>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "60%", padding: "6px 0" }}>Player</th>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "15%", textAlign: "center", padding: "6px 0" }}>Pos</th>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "15%", textAlign: "right", padding: "6px 0" }}>Points</th>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "1%", textAlign: "right", padding: "6px 0" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {squadWithPoints.map((p) => (
                            <tr
                              key={p.id}
                              onClick={() => openPointsModal(p.id, squadModalPlayerIDs)}
                              style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: p.isCaptain ? "rgba(255,215,0,0.08)" : "transparent" }}
                            >
                              <td style={{ border: "none", width: "60%", padding: "10px 0" }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPointsModal(p.id, squadModalPlayerIDs);
                                  }}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    cursor: "pointer",
                                    textAlign: "left",
                                    font: "inherit",
                                    color: "inherit",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  {p.name}
                                  {p.isCaptain && (
                                    <span style={{ background: "#FFD700", color: "#7a5a00", borderRadius: "999px", fontSize: "0.68rem", fontWeight: "700", padding: "1px 6px", lineHeight: "1.5" }}>C</span>
                                  )}
                                  {dreamTeamSet.has(p.id) && (
                                    <Star size={11} fill="#f59e0b" color="#f59e0b" style={{ flexShrink: 0 }} aria-label="Dream Team" />
                                  )}
                                </button>
                              </td>
                              <td style={{ border: "none", width: "15%", textAlign: "center", padding: "10px 0" }}>{p.position}</td>
                              <td style={{ border: "none", width: "15%", textAlign: "right", padding: "10px 0", color: ptColor(p.final), fontWeight: "600" }}>{p.final}</td>
                              <td
                                style={{
                                  border: "none",
                                  width: "1%",
                                  textAlign: "right",
                                  paddingTop: "10px",
                                  paddingBottom: "10px",
                                  paddingLeft: "8px",
                                  paddingRight: 0,
                                  color: ptColor(p.final),
                                  fontWeight: "700",
                                }}
                              >
                                ›
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  <div style={{ marginTop: "1.5rem" }}>
                    <HeadingSmall style={{ fontWeight: "bold", marginBottom: "0.6rem" }}>
                      All Players • {selectedGW}
                    </HeadingSmall>
                    {/* Top 3 performers strip */}
                    {(() => {
                      const sorted = players
                        .slice()
                        .sort((a, b) => Number(playerPoints[b.id]?.total ?? 0) - Number(playerPoints[a.id]?.total ?? 0));
                      const top3 = sorted.slice(0, 3);
                      const medalBg = [
                        "rgba(255,215,0,0.15)",
                        "rgba(192,192,192,0.15)",
                        "rgba(205,127,50,0.15)",
                      ];
                      const trophyColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
                      return top3.length > 0 ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.4rem", marginBottom: "0.85rem" }}>
                          {top3.map((p, i) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => openPointsModal(p.id, allPlayersModalPlayerIDs)}
                              style={{ background: medalBg[i], border: "1px solid rgba(0,0,0,0.06)", borderRadius: "10px", padding: "0.55rem 0.4rem", textAlign: "center", cursor: "pointer", font: "inherit", color: "inherit", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", gap: "3px", minHeight: "90px" }}
                            >
                              <Trophy size={13} color={trophyColors[i]} style={{ flexShrink: 0 }} />
                              <div style={{ fontSize: "0.75rem", fontWeight: "600", lineHeight: 1.2, wordBreak: "break-word" }}>{p.name}</div>
                              <div style={{ fontSize: "0.68rem", color: "#9ca3af", background: "rgba(0,0,0,0.05)", borderRadius: "999px", padding: "1px 6px" }}>{p.position}</div>
                              <div style={{ fontSize: "0.9rem", fontWeight: "700", color: ptColor(Number(playerPoints[p.id]?.total ?? 0)) }}>{Number(playerPoints[p.id]?.total ?? 0)}</div>
                            </button>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    {players.length === 0 ? (
                      <BodyText>No matches yet for {selectedGW}</BodyText>
                    ) : (
                    <div style={{ width: "100%", overflowX: "auto" }}>
                      <table
                        cellPadding="0"
                        cellSpacing="0"
                        style={{ width: "100%", maxWidth: 600, borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr style={{ borderBottom: "none", background: "transparent" }}>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "60%", padding: "6px 0" }}>Player</th>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "15%", textAlign: "center", padding: "6px 0" }}>Pos</th>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "15%", textAlign: "right", padding: "6px 0" }}>Points</th>
                            <th style={{ border: "none", background: "transparent", whiteSpace: "nowrap", width: "1%", textAlign: "right", padding: "6px 0" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {players
                            .slice()
                            .sort((a, b) => (Number(playerPoints[b.id]?.total ?? 0) - Number(playerPoints[a.id]?.total ?? 0)))
                            .map((p) => {
                              const pts = Number(playerPoints[p.id]?.total ?? 0);
                              const col = ptColor(pts);
                              return (
                                <tr
                                  key={p.id}
                                  onClick={() => openPointsModal(p.id, allPlayersModalPlayerIDs)}
                                  style={{ borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                                >
                                  <td style={{ border: "none", width: "60%", padding: "10px 0" }}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPointsModal(p.id, allPlayersModalPlayerIDs);
                                      }}
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        padding: 0,
                                        cursor: "pointer",
                                        textAlign: "left",
                                        font: "inherit",
                                        color: "inherit",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                      }}
                                    >
                                      {p.name}
                                      {dreamTeamSet.has(p.id) && (
                                        <Star size={11} fill="#f59e0b" color="#f59e0b" style={{ flexShrink: 0 }} aria-label="Dream Team" />
                                      )}
                                    </button>
                                  </td>
                                  <td style={{ border: "none", width: "15%", textAlign: "center", padding: "10px 0" }}>{p.position}</td>
                                  <td style={{ border: "none", width: "15%", textAlign: "right", padding: "10px 0", color: col, fontWeight: "600" }}>{pts}</td>
                                  <td
                                    style={{
                                      border: "none",
                                      width: "1%",
                                      textAlign: "right",
                                      paddingTop: "10px",
                                      paddingBottom: "10px",
                                      paddingLeft: "8px",
                                      paddingRight: 0,
                                      color: col,
                                      fontWeight: "700",
                                    }}
                                  >
                                    ›
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </div>
                </Panel>
                )}
              </div>
            </div>
          )}
          {activeView === "pitch" && (
            <>
              <div style={{ marginTop: "0", position: "relative" }}>
                {(() => {
                  const pointsByIdForPitch = {};
                  squadWithPoints.forEach((p) => {
                    pointsByIdForPitch[p.id] = { total: p.final };
                  });
                  return (
                    <Pitch
                      team={selection}
                      playersById={new Map(Object.entries(playersById))}
                      positions={positions}
                      captainID={captainID}
                      pointsById={pointsByIdForPitch}
                      valueSuffix=" pts"
                      onPlayerClick={(playerId) => openPointsModal(playerId, squadModalPlayerIDs)}
                      dreamTeamSet={dreamTeamSet.size > 0 ? dreamTeamSet : null}
                    />
                  );
                })()}
              </div>
              {!noSquad && (
                <div style={{ width: "100%", maxWidth: "420px", margin: "0.75rem auto 0", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "0.85rem 0.95rem", boxSizing: "border-box", boxShadow: "0 6px 18px rgba(0,0,0,0.06)" }}>
                  {/* Tier 1 — Hero score */}
                  <div style={{ textAlign: "center", marginBottom: "0.65rem" }}>
                    <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "42px", fontWeight: "800", lineHeight: 1, color: "#222", letterSpacing: "-1px" }}>
                      {total}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: "3px", fontWeight: 500 }}>
                      {selectedGW} Points{gwManagerRank ? ` • #${gwManagerRank} this GW` : ""}
                    </div>
                  </div>
                  {/* Tier 2 — Supporting pills */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem", justifyContent: "center" }}>
                    <div style={{ background: "#f4f7f8", borderRadius: "999px", padding: "0.35rem 0.65rem", fontSize: "0.82rem", fontWeight: "600", color: "#234", whiteSpace: "nowrap" }}>
                      C: {players.find(p => p.id === captainID)?.name || "—"} · {squadWithPoints.find(p => p.id === captainID)?.final ?? 0} pts
                    </div>
                    {chipUsed && chipUsed !== "None" && (
                      <div style={{ background: "#f4f7f8", borderRadius: "999px", padding: "0.35rem 0.65rem", fontSize: "0.82rem", fontWeight: "600", color: "#234", whiteSpace: "nowrap" }}>
                        {chipUsed}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      <PlayerPointsModal
        open={pointsModalOpen}
        onClose={closePointsModal}
        leagueID={leagueID}
        player={activePointsPlayer}
        onPrev={goToPrevModalPlayer}
        onNext={goToNextModalPlayer}
        canPrev={canPrevModalPlayer}
        canNext={canNextModalPlayer}
        currentGameweek={selectedGW}
        gwLabel={selectedGW}
        chipName={chipUsed}
        playerGWPoints={{
          ...(activePointsDoc || {}),
          isGK: Boolean(activePointsBreakdown?.isGK || activePointsRaw?.isGK),
          goals: Number(activePointsRaw.goals || 0),
          assists: Number(activePointsRaw.assists || 0),
          cleanSheets: Number(activePointsRaw.cleanSheet || activePointsRaw.cs || 0),
          bonus: Number(activePointsRaw.bonus || 0),
          yellowCards: Number(activePointsRaw.yc || activePointsRaw.yellowCards || 0),
          saves: Number(activePointsRaw.saves || 0),
          basePoints: Number(activePointsChipCalc.base || 0),
          finalPoints: Number(activePointsChipCalc.final || 0),
        }}
        breakdownRows={activePointsBreakdownRows}
        isCaptain={activePointsIsCaptain}
        isSelected={flatIds.includes(activePointsPlayerID)}
        gwRank={activePointsPlayer ? gwRankMap.get(activePointsPlayer.id) : null}
        positionRank={activePointsPlayer ? positionGwRankMap.get(activePointsPlayer.id) : null}
        formRank={activePointsPlayer ? formRankMap.get(activePointsPlayer.id) : null}
        seasonRank={activePointsPlayer ? seasonRankMap.get(activePointsPlayer.id) : null}
        seasonPositionRank={activePointsPlayer ? seasonPositionRankMap.get(activePointsPlayer.id) : null}
        basePoints={Number(activePointsChipCalc.base || 0)}
        finalPoints={Number(activePointsChipCalc.final || 0)}
      />
    </>
  );
}